import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://projectmanagementtoolbackend-production.up.railway.app';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const taskService = {
  async getAll(
    projectId?: string,
    assignedToId?: string,
    options?: { all?: boolean; limit?: number; taskType?: string }
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (projectId) params.append('projectId', projectId);
    if (assignedToId) params.append('assignedToId', assignedToId);
    if (options?.all) params.append('all', 'true');
    if (options?.limit !== undefined) params.append('limit', String(options.limit));
    if (options?.taskType) params.append('taskType', options.taskType);

    const queryString = params.toString();
    const url = queryString ? `${API_URL}/tasks?${queryString}` : `${API_URL}/tasks`;
    const response = await axios.get(url, getAuthHeaders());
    return response.data;
  },

  async getByProject(projectId: string): Promise<any[]> {
    return this.getAll(projectId);
  },

  async getOne(id: string): Promise<any> {
    const response = await axios.get(`${API_URL}/tasks/${id}`, getAuthHeaders());
    return response.data;
  },

  async updateStatus(
    id: string,
    status: string,
    isCompleted?: boolean,
    fileUrl?: string,
    deliverableType?: string,
    deliverableId?: string,
    reviewIntent?: 'for_approval' | 'revision',
  ): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}/status`,
      { status, isCompleted, fileUrl, deliverableType, deliverableId, reviewIntent },
      getAuthHeaders()
    );
    return response.data;
  },

  async assign(id: string, assignedToId: string): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}/assign`,
      { assignedToId },
      getAuthHeaders()
    );
    return response.data;
  },

  async assignMultiple(id: string, userIds: string[]): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}/assign`,
      { userIds },
      getAuthHeaders()
    );
    return response.data;
  },

  async create(createTaskDto: any): Promise<any> {
    const response = await axios.post(
      `${API_URL}/tasks`,
      createTaskDto,
      getAuthHeaders()
    );
    return response.data;
  },

  async submitOnboardingData(id: string, submissionData: string, submissionType: 'url' | 'text'): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}/submit`,
      { submissionData, submissionType },
      getAuthHeaders()
    );
    return response.data;
  },

  async update(id: string, updateData: { title?: string; description?: string; baseDescription?: string | null; dueDate?: Date | null; deliverableId?: string }): Promise<any> {
    const response = await axios.put(
      `${API_URL}/tasks/${id}`,
      updateData,
      getAuthHeaders()
    );
    return response.data;
  },

  async getActivity(id: string): Promise<any[]> {
    const response = await axios.get(`${API_URL}/tasks/${id}/activity`, getAuthHeaders());
    return response.data;
  },

  async updateMovedDueDate(
    id: string,
    payload: { movedDate: Date | string; comment?: string }
  ): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}/moved-due-date`,
      payload,
      getAuthHeaders()
    );
    return response.data;
  },

  async delete(id: string): Promise<any> {
    const response = await axios.delete(
      `${API_URL}/tasks/${id}`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Task Conversation Methods
  async getConversations(taskId: string): Promise<any[]> {
    const response = await axios.get(
      `${API_URL}/tasks/${taskId}/conversations`,
      getAuthHeaders()
    );
    return response.data;
  },

  async getAllConversations(): Promise<any[]> {
    const response = await axios.get(
      `${API_URL}/tasks/conversations/all`,
      getAuthHeaders()
    );
    return response.data;
  },

  async createQuestion(taskId: string, text: string, mentionedUserIds?: string[]): Promise<any> {
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/questions`,
      { text, mentionedUserIds },
      getAuthHeaders()
    );
    return response.data;
  },

  async createComment(questionId: string, text: string, mentionedUserIds?: string[]): Promise<any> {
    const response = await axios.post(
      `${API_URL}/tasks/questions/${questionId}/comments`,
      { text, mentionedUserIds },
      getAuthHeaders()
    );
    return response.data;
  },

  async deleteQuestion(questionId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/tasks/questions/${questionId}`,
      getAuthHeaders()
    );
  },

  async transferTask(
    id: string,
    payload: { toDepartment: string; kind: 'forward' | 'return'; note?: string; toAssigneeIds?: string[] },
  ): Promise<any> {
    const response = await axios.post(
      `${API_URL}/tasks/${id}/transfer`,
      payload,
      getAuthHeaders()
    );
    return response.data;
  },

  async getTransfers(id: string): Promise<any[]> {
    const response = await axios.get(
      `${API_URL}/tasks/${id}/transfers`,
      getAuthHeaders()
    );
    return response.data;
  },

  async getAttachments(taskId: string): Promise<any[]> {
    const response = await axios.get(
      `${API_URL}/tasks/${taskId}/attachments`,
      getAuthHeaders()
    );
    return response.data;
  },

  async uploadAttachments(taskId: string, files: File[], note?: string): Promise<any[]> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    if (note?.trim()) formData.append('note', note.trim());
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/attachments`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  async addLinkAttachment(taskId: string, url: string, label?: string, note?: string): Promise<any> {
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/attachments/link`,
      { url, label, note },
      getAuthHeaders()
    );
    return response.data;
  },

  async deleteAttachment(taskId: string, attachmentId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/tasks/${taskId}/attachments/${attachmentId}`,
      getAuthHeaders()
    );
  },
};

